import { useMemo, useState } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import { cn } from '../../../../lib/cn.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import {
  RolePill,
  StaffDetailPanel,
  StatusBadge,
} from '../../../../components/admin/StaffDetailPanel.js';
import { useStaffList, type StaffUser } from '../../../../hooks/useStaff.js';
import { ASSIGNABLE_ROLES, ROLE_LABELS } from '../../../../lib/capability-view.js';
import { Input } from '../../../../components/ui/Input.js';
import { Select } from '../../../../components/ui/Select.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/staff',
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'super_admin') throw redirect({ to: '/app' });
  },
  component: StaffPage,
});

function StaffPage() {
  const { data, isLoading, error, refetch } = useStaffList();
  const users = data?.users ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = users.find((u) => u.id === selectedId) ?? null;

  return (
    <div>
      <AdminPageHeader
        title="Staff"
        description="Set a person's role and fine-tune their permissions. Changes apply immediately."
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-start">
        <StaffList
          users={users}
          isLoading={isLoading}
          error={(error as Error | null) ?? null}
          onRetry={() => refetch()}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        {selected ? (
          <StaffDetailPanel key={selected.id} staff={selected} />
        ) : (
          <div className="surface p-10 text-center text-sm text-muted">
            Select a person to manage their access.
          </div>
        )}
      </div>
    </div>
  );
}

function StaffList({
  users,
  isLoading,
  error,
  onRetry,
  selectedId,
  onSelect,
}: {
  users: StaffUser[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [office, setOffice] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Distinct offices present in the list (for the filter dropdown).
  const offices = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) if (u.office) map.set(u.office.code, u.office.name);
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => {
        if (q && !u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
        if (office === '__none__' && u.office) return false;
        if (office && office !== '__none__' && u.office?.code !== office) return false;
        if (roleFilter && u.role !== roleFilter) return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, search, office, roleFilter]);

  return (
    <div className="surface p-4">
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={office} onChange={(e) => setOffice(e.target.value)} className="sm:w-44">
          <option value="">All offices</option>
          <option value="__none__">No office</option>
          {offices.map(([code, name]) => (
            <option key={code} value={code}>
              {name} ({code})
            </option>
          ))}
        </Select>
        <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="sm:w-40">
          <option value="">All roles</option>
          <option value="super_admin">{ROLE_LABELS.super_admin}</option>
          {ASSIGNABLE_ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </Select>
      </div>

      <div className="mt-4">
        {isLoading && <p className="px-1 py-6 text-sm text-muted">Loading staff…</p>}
        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
            Couldn't load staff: {error.message}{' '}
            <button className="underline" onClick={onRetry}>
              Retry
            </button>
          </div>
        )}
        {!isLoading && !error && filtered.length === 0 && (
          <p className="px-1 py-6 text-sm text-muted">No staff match.</p>
        )}
        {!isLoading && !error && filtered.length > 0 && (
          <ul className="divide-y divide-line">
            {filtered.map((u) => (
              <li key={u.id}>
                <button
                  onClick={() => onSelect(u.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors',
                    selectedId === u.id ? 'bg-cream-deep/60' : 'hover:bg-cream/50',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{u.name}</span>
                    <span className="block truncate text-xs text-muted">
                      {u.office ? u.office.code : '—'}
                      {u.agency ? ` · ${u.agency.name}` : ''}
                    </span>
                  </span>
                  <RolePill role={u.role} />
                  {!u.active && <StatusBadge active={false} />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
